/**
 * Reminder ladder (§22).
 *
 * Offsets are expressed in days relative to an installment's due date:
 * negative is before, zero is the due date, positive is after. Administrators
 * can reconfigure the ladder at runtime; the values below are what gets seeded.
 *
 * Nothing here charges anything. An overdue Qard Hasan installment accrues no
 * penalty — these stages exist to prompt, not to price.
 */
import { NotificationChannel, NotificationEvent } from './enums.js';
export const DEFAULT_REMINDER_LADDER = [
    {
        key: 'due_in_14_days',
        offsetDays: -14,
        label: '14 days before due date',
        channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
        event: NotificationEvent.PAYMENT_DUE_SOON,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'due_in_7_days',
        offsetDays: -7,
        label: '7 days before due date',
        channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
        event: NotificationEvent.PAYMENT_DUE_SOON,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'due_in_3_days',
        offsetDays: -3,
        label: '3 days before due date',
        channels: [NotificationChannel.EMAIL, NotificationChannel.SMS, NotificationChannel.IN_APP],
        event: NotificationEvent.PAYMENT_DUE_SOON,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'due_in_1_day',
        offsetDays: -1,
        label: '1 day before due date',
        channels: [NotificationChannel.SMS, NotificationChannel.WHATSAPP, NotificationChannel.IN_APP],
        event: NotificationEvent.PAYMENT_DUE_SOON,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'due_today',
        offsetDays: 0,
        label: 'On the due date',
        channels: [NotificationChannel.SMS, NotificationChannel.EMAIL, NotificationChannel.IN_APP],
        event: NotificationEvent.PAYMENT_DUE_TODAY,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'overdue_1_day',
        offsetDays: 1,
        label: '1 day overdue',
        channels: [NotificationChannel.SMS, NotificationChannel.IN_APP],
        event: NotificationEvent.PAYMENT_OVERDUE,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'overdue_3_days',
        offsetDays: 3,
        label: '3 days overdue',
        channels: [NotificationChannel.SMS, NotificationChannel.EMAIL, NotificationChannel.IN_APP],
        event: NotificationEvent.PAYMENT_OVERDUE,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'overdue_7_days',
        offsetDays: 7,
        label: '7 days overdue',
        channels: [NotificationChannel.SMS, NotificationChannel.WHATSAPP, NotificationChannel.EMAIL],
        event: NotificationEvent.PAYMENT_OVERDUE,
        notifyGuarantors: false,
        enabled: true,
    },
    {
        key: 'overdue_14_days',
        offsetDays: 14,
        label: '14 days overdue',
        channels: [NotificationChannel.SMS, NotificationChannel.EMAIL, NotificationChannel.WHATSAPP],
        event: NotificationEvent.PAYMENT_OVERDUE,
        // Guarantors are brought in once arrears are sustained — the substitute for
        // a late fee under a no-penalty policy.
        notifyGuarantors: true,
        enabled: true,
    },
    {
        key: 'overdue_30_days',
        offsetDays: 30,
        label: '30 days overdue',
        channels: [NotificationChannel.SMS, NotificationChannel.EMAIL, NotificationChannel.WHATSAPP],
        event: NotificationEvent.PAYMENT_OVERDUE,
        notifyGuarantors: true,
        enabled: true,
    },
];
/** Days overdue after which a loan is proposed for DEFAULTED status. Configurable. */
export const DEFAULT_ARREARS_DEFAULT_THRESHOLD_DAYS = 90;
/** Template variables available to administrators (§25). */
export const TEMPLATE_VARIABLES = [
    'member_name',
    'member_first_name',
    'membership_number',
    'loan_number',
    'amount_due',
    'outstanding_balance',
    'total_repayable',
    'due_date',
    'days_overdue',
    'installment_number',
    'installment_count',
    'organization_name',
    'organization_phone',
    'organization_email',
    'portal_url',
    'guarantor_name',
    'borrower_name',
];
const VARIABLE_PATTERN = /\{\{\s*([a-z_]+)\s*\}\}/g;
/** Substitutes `{{variable}}` placeholders. Unknown placeholders are left intact. */
export function renderTemplate(template, values) {
    return template.replace(VARIABLE_PATTERN, (match, name) => {
        const value = values[name];
        return value ?? match;
    });
}
/** Reports placeholders a template uses that aren't in the supported list. */
export function findUnknownVariables(template) {
    const known = new Set(TEMPLATE_VARIABLES);
    const unknown = new Set();
    for (const match of template.matchAll(VARIABLE_PATTERN)) {
        const name = match[1];
        if (!known.has(name))
            unknown.add(name);
    }
    return [...unknown];
}
/**
 * SMS length guard. Nigerian providers bill per 160-character GSM-7 segment,
 * and any non-GSM character silently drops the whole message to 70-character
 * UCS-2 segments — a 4× cost increase that is easy to miss.
 */
export function estimateSmsSegments(message) {
    // eslint-disable-next-line no-control-regex
    const gsm7 = /^[\x20-\x7E\r\n£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉÄÖÑÜ§¿äöñüà€]*$/;
    const isGsm7 = gsm7.test(message);
    const perSegment = isGsm7 ? 160 : 70;
    const perConcatenated = isGsm7 ? 153 : 67;
    const length = message.length;
    const segments = length <= perSegment ? Math.max(1, Math.ceil(length / perSegment)) : Math.ceil(length / perConcatenated);
    return { segments, encoding: isGsm7 ? 'GSM7' : 'UCS2' };
}
/**
 * Characters that silently quadruple the cost of an SMS.
 *
 * A message made only of GSM-7 characters fits 160 per segment. One character
 * outside that alphabet forces the whole message into UCS-2, where a segment
 * holds 70 — so a single naira sign can turn a one-part message into three.
 *
 * The naira sign is the one that matters here: every amount is rendered by
 * formatNaira, so without this substitution every price-bearing SMS the system
 * has ever sent would have been billed at more than double. The rest are
 * typographic characters that creep in from copy written for the web.
 */
const GSM7_SUBSTITUTIONS = [
    [/₦/g, 'NGN '], // ₦
    [/[‘’ʼ]/g, "'"], // ' ' ʼ
    [/[“”]/g, '"'], // " "
    [/[–—]/g, '-'], // – —
    [/…/g, '...'], // …
    [/ /g, ' '], // non-breaking space
    [/•/g, '*'], // •
];
/**
 * Rewrites a message so it stays inside the GSM-7 alphabet.
 *
 * Applied to SMS bodies only. Email and WhatsApp render Unicode properly and
 * should keep the naira sign — this is a billing concern, not a display one.
 * Anything still outside GSM-7 afterwards is left alone rather than mangled;
 * estimateSmsSegments will price it honestly.
 */
export function toGsm7(text) {
    return GSM7_SUBSTITUTIONS.reduce((out, [pattern, replacement]) => out.replace(pattern, replacement), text);
}
//# sourceMappingURL=reminders.js.map